import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"

export interface Option {
  value: string
  label: string
}

export interface CustomerOption {
  id: number
  name: string
}

export interface ParentOption {
  id: number
  name: string
  customer_id: number
}

export interface FormValues {
  customer_id: string
  parent_document_id: string
  document_type: string
  status: string
  title: string
  effective_date: string
  execution_date: string
  end_date: string
  governing_law: string
  currency: string
  document_url: string
  notes: string
}

interface Props {
  values: FormValues
  errors: Partial<Record<keyof FormValues, string>>
  customers: CustomerOption[]
  documentTypes: Option[]
  statuses: Option[]
  currencies: string[]
  parentOptions: ParentOption[]
  processing: boolean
  submitLabel: string
  cancelHref: string
  onChange: (field: keyof FormValues, value: string) => void
  onSubmit: (e: React.FormEvent) => void
}

const NONE = "none"

export default function LegalDocumentForm({
  values,
  errors,
  customers,
  documentTypes,
  statuses,
  currencies,
  parentOptions,
  processing,
  submitLabel,
  cancelHref,
  onChange,
  onSubmit,
}: Props) {
  const allErrors = Object.values(errors).filter(Boolean)

  // A SOW hangs off the MSA signed with the same customer, so the list narrows
  // to that customer once one is picked.
  const availableParents = values.customer_id
    ? parentOptions.filter((option) => String(option.customer_id) === values.customer_id)
    : parentOptions

  return (
    <form onSubmit={onSubmit}>
      <div className="rounded-xl border bg-card">
        <div className="p-6">
          {allErrors.length > 0 && (
            <div className="border-destructive/20 bg-destructive/5 mb-6 rounded-lg border p-3">
              <ul className="text-destructive space-y-1 text-sm">
                {allErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={values.title}
                onChange={(e) => onChange("title", e.target.value)}
                placeholder="Master Services Agreement"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="customer_id">Customer</Label>
                <Select value={values.customer_id} onValueChange={(v) => onChange("customer_id", v)}>
                  <SelectTrigger id="customer_id">
                    <SelectValue placeholder="Select a customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={String(customer.id)}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="document_type">Type</Label>
                <Select value={values.document_type} onValueChange={(v) => onChange("document_type", v)}>
                  <SelectTrigger id="document_type">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={values.status} onValueChange={(v) => onChange("status", v)}>
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="parent_document_id">Parent agreement</Label>
                <Select
                  value={values.parent_document_id || NONE}
                  onValueChange={(v) => onChange("parent_document_id", v === NONE ? "" : v)}
                >
                  <SelectTrigger id="parent_document_id">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {availableParents.map((option) => (
                      <SelectItem key={option.id} value={String(option.id)}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">The MSA this document was executed under, if any.</p>
              </div>
            </div>

            <Separator />

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="effective_date">Effective date</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={values.effective_date}
                  onChange={(e) => onChange("effective_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="execution_date">Execution date</Label>
                <Input
                  id="execution_date"
                  type="date"
                  value={values.execution_date}
                  onChange={(e) => onChange("execution_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">End date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={values.end_date}
                  onChange={(e) => onChange("end_date", e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="governing_law">Governing law</Label>
                <Input
                  id="governing_law"
                  value={values.governing_law}
                  onChange={(e) => onChange("governing_law", e.target.value)}
                  placeholder="Illinois, USA"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={values.currency || NONE}
                  onValueChange={(v) => onChange("currency", v === NONE ? "" : v)}
                >
                  <SelectTrigger id="currency">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {currencies.map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {currency.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="document_url">Document link</Label>
              <Input
                id="document_url"
                type="url"
                value={values.document_url}
                onChange={(e) => onChange("document_url", e.target.value)}
                placeholder="https://drive.google.com/..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={4} value={values.notes} onChange={(e) => onChange("notes", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t p-4">
          <Button variant="ghost" type="button" asChild>
            <a href={cancelHref}>Cancel</a>
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? "Saving..." : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}
